/**
 * @file PlanChangeSheet.tsx
 * @brief Confirmation for switching plan or interval while a plan is live.
 *
 * @details
 * POST /billing/subscriptions/change (BACKEND A2, 18-10-2026) prorates with
 * `always_invoice`: moving up charges the difference now (3D Secure if the bank
 * asks, then /subscriptions/sync), moving down credits the rest. The sheet says
 * which, and shows the new price and interval; "Plan changed" appears only when
 * the server reports the new plan active with nothing pending
 * (planConfirm.changeOutcome). A change also undoes a pending cancel.
 *
 * @author DES
 * @date 18-10-2026
 */

import { CircleCheck, LoaderCircle, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { confirmWithBank } from "@/shared/stripe/confirmWithBank";
import { cn } from "@/shared/theme/cn";
import { changeMyPlan, myBillingKeys, syncMySubscription, useChangePreview } from "../api";
import { billingErrorView, isFinalRefusal } from "../billingErrors";
import { buildChangeConfirmation, changeMoneyLine, changeOutcome, type BillingInterval, type CatalogPlan } from "../planConfirm";
import { vatMode } from "./format";

type Phase =
  | { kind: "confirm" }
  | { kind: "working"; note: string }
  | { kind: "done" }
  | { kind: "error"; title: string; message: string; final?: boolean };

const CODE_MESSAGES: Record<string, string> = {
  PLAN_PAST_DUE: "Your last payment failed. Update your card first, then change plan.",
  SAME_PLAN: "That's already your plan.",
  PLAN_CHANGE_DECLINED: "Your card was declined for the difference. Nothing changed.",
  PLAN_CHANGE_UNAVAILABLE: "This plan can't be changed right now. Please try again later.",
  ANNUAL_NOT_AVAILABLE: "Yearly billing isn't available for this plan.",
};

export function PlanChangeSheet({
  from,
  to,
  cancelling,
  onOpenChange,
}: {
  from: { plan: CatalogPlan; interval: BillingInterval } | null;
  to: { plan: CatalogPlan; interval: BillingInterval } | null;
  cancelling: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  const open = !!(from && to);
  const c = from && to ? buildChangeConfirmation(from, to.plan, to.interval, vatMode) : null;
  const busy = phase.kind === "working";
  // The exact proration when the server can preview it; else the rule.
  const preview = useChangePreview(open && to ? { planCode: to.plan.code, interval: to.interval } : null);
  const money = changeMoneyLine(preview.data);

  useEffect(() => {
    if (open) setPhase({ kind: "confirm" });
  }, [open, to?.plan.code, to?.interval]);

  const run = async () => {
    if (!to) return;
    setPhase({ kind: "working", note: "Changing your plan…" });
    try {
      let res = await changeMyPlan({ planCode: to.plan.code, interval: to.interval });
      let outcome = changeOutcome(res, { planCode: to.plan.code, interval: to.interval });
      if (outcome.kind === "needs_action") {
        setPhase({ kind: "working", note: "Waiting for your bank to confirm…" });
        // One bank check at a time (confirmWithBank, 21-10-2026).
        const bank = await confirmWithBank(outcome.clientSecret, res.subscriptionId ?? "");
        if (!bank.ok) {
          const v = billingErrorView(bank.code, "personal");
          setPhase({ kind: "error", title: v?.title ?? "Plan not changed", message: bank.message });
          return;
        }
        if (res.subscriptionId) res = await syncMySubscription(res.subscriptionId);
        outcome = changeOutcome({ ...res, clientSecret: null }, { planCode: to.plan.code, interval: to.interval });
      }
      qc.setQueryData(myBillingKeys.status(), res);
      void qc.invalidateQueries({ queryKey: myBillingKeys.all });
      if (outcome.kind === "done") {
        setPhase({ kind: "done" });
      } else {
        setPhase({
          kind: "error",
          title: "Not changed yet",
          message: "Stripe hasn't confirmed the change, so your plan is as before. Check your card, then try again.",
        });
      }
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : undefined;
      // Blocked country / plans paused (B1): the server's sentence, no retry.
      const view = isFinalRefusal(code) ? billingErrorView(code, "personal", err instanceof Error ? err.message : null) : null;
      setPhase({
        kind: "error",
        title: view?.title ?? "Plan not changed",
        final: !!view,
        message:
          view?.message ||
          (code && CODE_MESSAGES[code]) ||
          (err instanceof Error ? err.message : "We couldn't change your plan. Please try again."),
      });
    }
  };

  return (
    <FormSheet open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>{phase.kind === "done" ? "Plan changed" : `Switch to ${c?.heading ?? "plan"}`}</FormSheetTitle>
            <FormSheetDescription>
              {phase.kind === "done" ? "Stripe confirmed the change." : `From ${from?.plan.name ?? ""}, ${from?.interval === "year" ? "yearly" : "monthly"}.`}
            </FormSheetDescription>
          </FormSheetHeader>
          <FormSheetBody>
            {phase.kind === "done" ? (
              <div className="flex items-start gap-3 px-4 py-4 md:px-5">
                <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                <p className="text-sm">You're on {to?.plan.name}. Any charge or credit is in Invoices.</p>
              </div>
            ) : (
              <FormSheetSection title="What happens">
                <div className="space-y-1 px-4 py-4 text-sm md:px-5" data-testid="plan-change-lines">
                  <p>{preview.isLoading ? "Working out what you pay now…" : (money?.line ?? c?.nowLine)}</p>
                  <p className="text-muted-foreground">{c?.renewLine}</p>
                  {cancelling ? <p className="text-muted-foreground">Your cancellation is undone.</p> : null}
                </div>
              </FormSheetSection>
            )}
            {phase.kind === "error" ? (
              <div className="flex items-start gap-3 border-b border-r border-border/60 bg-destructive/5 px-4 py-4 md:px-5" role="alert">
                <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div className="text-sm">
                  <p className="font-semibold">{phase.title}</p>
                  <p className="mt-0.5 text-muted-foreground">{phase.message}</p>
                </div>
              </div>
            ) : null}
          </FormSheetBody>
          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                disabled={busy || !c || preview.isLoading}
                onClick={() => (phase.kind === "done" || (phase.kind === "error" && phase.final) ? onOpenChange(false) : void run())}
                className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
              >
                {busy ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    {phase.note}
                  </>
                ) : phase.kind === "done" ? (
                  "Done"
                ) : phase.kind === "error" ? (
                  phase.final ? "Close" : "Try again"
                ) : (
                  (money?.payLabel ?? c?.actionLabel ?? "Switch")
                )}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
